import { z } from 'zod';

export const CUSTOM_SIGNAL_TYPE = 'company.rule_matched';
export const SIGNAL_RULE_LIMIT = 20;
export const SIGNAL_RULE_INTERVAL_MS = 15 * 60 * 1000;

export const signalConditionSchema = z
  .object({
    metric: z.enum(['events', 'users', 'growth']),
    source: z.enum(['any', 'event', 'page']),
    event_name: z.string().trim().max(160).optional(),
    path: z.string().trim().max(512).optional(),
    path_match: z.enum(['exact', 'prefix']).default('prefix'),
    window_hours: z.number().int().min(1).max(720).default(168),
    minimum: z.number().int().min(1).max(1_000_000_000).default(1),
    multiplier: z.number().min(1.01).max(1000).default(3),
  })
  .superRefine((value, ctx) => {
    if (value.source === 'event' && !value.event_name) {
      ctx.addIssue({
        code: 'custom',
        path: ['event_name'],
        message: 'Choose an event name.',
      });
    }
    if (
      value.source === 'page' &&
      (!value.path?.startsWith('/') || /[?#]/.test(value.path))
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['path'],
        message:
          'Use a page path starting with /, without a query or fragment.',
      });
    }
  });
export type SignalCondition = z.infer<typeof signalConditionSchema>;

export const signalCompanyFilterSchema = z
  .object({
    field: z
      .string()
      .trim()
      .min(1)
      .max(100)
      .regex(/^[a-zA-Z0-9_]+$/),
    operator: z.enum(['equals', 'contains', 'gte', 'lte']),
    value: z.string().trim().min(1).max(512),
  })
  .refine(
    (filter) =>
      !['gte', 'lte'].includes(filter.operator) ||
      Number.isFinite(Number(filter.value)),
    {
      path: ['value'],
      message: 'Enter a number for a numeric comparison.',
    },
  );

export const signalRuleDefinitionSchema = z.object({
  project_ids: z.array(z.string().uuid()).max(100).default([]),
  scope: z.enum(['all', 'targets']).default('all'),
  match: z.enum(['all', 'any']).default('all'),
  conditions: z.array(signalConditionSchema).min(1).max(5),
  company_filters: z.array(signalCompanyFilterSchema).max(5).default([]),
});
export type SignalRuleDefinition = z.infer<typeof signalRuleDefinitionSchema>;

export const saveSignalRuleSchema = z.object({
  name: z.string().trim().min(1).max(160),
  definition: signalRuleDefinitionSchema,
  cooldown_hours: z.number().int().min(0).max(720).default(24),
  enabled: z.boolean().default(false),
});
export type SaveSignalRule = z.infer<typeof saveSignalRuleSchema>;

export const signalRuleSchema = saveSignalRuleSchema.extend({
  id: z.string().uuid(),
  version: z.number().int(),
  archived_at: z.string().nullable(),
  last_evaluated_at: z.string().nullable(),
  last_error: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SignalRule = z.infer<typeof signalRuleSchema>;

export const signalConditionEvidenceSchema = z.object({
  condition: signalConditionSchema,
  current: z.number().nonnegative(),
  previous: z.number().nonnegative(),
  matched: z.boolean(),
  window_start: z.string(),
  window_end: z.string(),
});
export type SignalConditionEvidence = z.infer<
  typeof signalConditionEvidenceSchema
>;
export const signalRulePreviewSchema = z.object({
  evaluated_at: z.string(),
  total: z.number(),
  companies: z.array(
    z.object({
      company_id: z.string(),
      company_name: z.string(),
      evidence: z.array(signalConditionEvidenceSchema),
    }),
  ),
});
export type SignalRulePreview = z.infer<typeof signalRulePreviewSchema>;

export function signalConditionSummary(
  evidence: SignalConditionEvidence,
): string {
  const { condition, current, previous } = evidence;
  const subject =
    condition.source === 'page'
      ? condition.path
      : condition.source === 'event'
        ? condition.event_name
        : 'All activity';
  const metric = condition.metric === 'users' ? 'identified users' : 'events';
  const growth =
    condition.metric === 'growth'
      ? `; previous ${previous}, required ${condition.multiplier}×`
      : '';
  return `${subject}: ${current} ${metric} in ${condition.window_hours}h (minimum ${condition.minimum}${growth})`;
}

export const SIGNAL_RULE_TEMPLATES: Array<{
  label: string;
  rule: SaveSignalRule;
}> = [
  {
    label: 'Repeated Pricing Visits',
    rule: saveSignalRuleSchema.parse({
      name: 'Repeated Pricing Visits',
      definition: {
        conditions: [
          { metric: 'events', source: 'page', path: '/pricing', minimum: 3 },
        ],
      },
    }),
  },
  {
    label: 'Product Activation',
    rule: saveSignalRuleSchema.parse({
      name: 'Product Activation',
      definition: {
        conditions: [
          {
            metric: 'events',
            source: 'event',
            event_name: 'Feature Used',
            minimum: 3,
          },
        ],
      },
    }),
  },
  {
    label: 'Team Adoption',
    rule: saveSignalRuleSchema.parse({
      name: 'Team Adoption',
      definition: {
        conditions: [{ metric: 'users', source: 'any', minimum: 3 }],
      },
    }),
  },
  {
    label: 'Usage Growth',
    rule: saveSignalRuleSchema.parse({
      name: 'Usage Growth',
      definition: {
        conditions: [{ metric: 'growth', source: 'any', minimum: 50 }],
      },
    }),
  },
];
